import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { selectedServiceLabel } from "@/lib/packageLines";
import { X } from "lucide-react";

function defaultFollowUpDate(days = 14) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function PickPackageModal({
  open,
  pkg,
  services,
  staff,
  lang,
  requireStaff = true,
  initialSelections = [],
  confirmLabel,
  trackConsumption = false,
  selectionLocked = false,
  pendingSwapMode = false,
  defaultPendingDays = 14,
  onClose,
  onConfirm,
}) {
  const [selected, setSelected] = useState([]);
  const [staffByService, setStaffByService] = useState({});
  const [consumptionByService, setConsumptionByService] = useState({});
  const [swapRows, setSwapRows] = useState([]);

  const isAr = lang === "ar";
  const lockedIds = useMemo(
    () => (initialSelections || []).map((s) => s.service_id).filter(Boolean),
    [initialSelections],
  );
  const pickCount = selectionLocked
    ? Math.max(1, lockedIds.length)
    : Math.max(1, Number(pkg?.pick_count) || 1);

  const eligible = useMemo(() => {
    const ids = new Set(pkg?.service_ids || []);
    const fromCatalog = (services || []).filter((s) => ids.has(s.service_id));
    if (!selectionLocked) return fromCatalog;
    const seen = new Set(fromCatalog.map((s) => s.service_id));
    const merged = [...fromCatalog];
    (initialSelections || []).forEach((sel) => {
      if (!sel.service_id || seen.has(sel.service_id)) return;
      merged.push({
        service_id: sel.service_id,
        name_en: sel.name_en || sel.name || sel.service_id,
        name_ar: sel.name_ar || sel.name || sel.service_id,
      });
      seen.add(sel.service_id);
    });
    return merged;
  }, [pkg, services, selectionLocked, initialSelections]);

  useEffect(() => {
    if (!open) return;
    if (pendingSwapMode) {
      const rows = (initialSelections || []).map((s) => ({ ...s }));
      setSwapRows(rows);
      const staffMap = {};
      const consumptionMap = {};
      rows.forEach((s) => {
        if (s.service_id && s.employee_user_id) staffMap[s.service_id] = s.employee_user_id;
        if (s.service_id) {
          consumptionMap[s.service_id] = {
            done: s.status !== "pending",
            scheduled_date: s.scheduled_date || defaultFollowUpDate(defaultPendingDays),
          };
        }
      });
      setStaffByService(staffMap);
      setConsumptionByService(consumptionMap);
      setSelected(rows.map((s) => s.service_id).filter(Boolean));
      return;
    }
    const ids = selectionLocked
      ? lockedIds
      : (initialSelections || []).map((s) => s.service_id).filter(Boolean);
    const staffMap = {};
    const consumptionMap = {};
    (initialSelections || []).forEach((s) => {
      if (s.service_id && s.employee_user_id) staffMap[s.service_id] = s.employee_user_id;
      if (s.service_id) {
        const isPending = s.status === "pending";
        consumptionMap[s.service_id] = {
          done: !isPending,
          scheduled_date: s.scheduled_date || defaultFollowUpDate(defaultPendingDays),
        };
      }
    });
    ids.forEach((sid) => {
      if (!consumptionMap[sid]) {
        consumptionMap[sid] = { done: true, scheduled_date: defaultFollowUpDate(defaultPendingDays) };
      }
    });
    setSelected(ids);
    setStaffByService(staffMap);
    setConsumptionByService(consumptionMap);
  }, [open, pkg?.package_id, initialSelections, defaultPendingDays, selectionLocked, lockedIds, pendingSwapMode]);

  const redeemedIds = useMemo(() => {
    if (!pendingSwapMode) return new Set();
    return new Set(
      (swapRows || [])
        .filter((r) => r.status !== "pending")
        .map((r) => r.service_id),
    );
  }, [pendingSwapMode, swapRows]);

  if (!open || !pkg) return null;

  const optionsForPendingRow = (row) => {
    const usedByOtherPending = new Set(
      (swapRows || [])
        .filter((r) => r.status === "pending" && r.service_id !== row.service_id)
        .map((r) => r.service_id),
    );
    return eligible.filter((s) => {
      if (redeemedIds.has(s.service_id)) return false;
      if (usedByOtherPending.has(s.service_id) && s.service_id !== row.service_id) return false;
      return true;
    });
  };

  const updateSwapRow = (oldServiceId, patch) => {
    setSwapRows((rows) => rows.map((r) => (
      r.service_id === oldServiceId ? { ...r, ...patch } : r
    )));
    if (patch.service_id && patch.service_id !== oldServiceId) {
      setStaffByService((m) => {
        const next = { ...m };
        if (next[oldServiceId] != null) {
          next[patch.service_id] = next[oldServiceId];
          delete next[oldServiceId];
        }
        return next;
      });
    }
  };

  const toggle = (serviceId) => {
    if (selectionLocked) return;
    setSelected((prev) => {
      if (prev.includes(serviceId)) return prev.filter((id) => id !== serviceId);
      if (prev.length >= pickCount) return prev;
      return [...prev, serviceId];
    });
    setConsumptionByService((m) => ({
      ...m,
      [serviceId]: m[serviceId] || { done: true, scheduled_date: defaultFollowUpDate(defaultPendingDays) },
    }));
  };

  const setDone = (serviceId, done) => {
    setConsumptionByService((m) => ({
      ...m,
      [serviceId]: {
        done,
        scheduled_date: m[serviceId]?.scheduled_date || defaultFollowUpDate(defaultPendingDays),
      },
    }));
  };

  const requiredCount = pendingSwapMode
    ? (swapRows || []).length
    : selectionLocked ? lockedIds.length : pickCount;
  const canConfirm = pendingSwapMode
    ? (swapRows || []).length > 0
      && (swapRows || []).every((r) => r.status !== "pending" || (
        r.service_id
        && (!requireStaff || staffByService[r.service_id])
      ))
    : selected.length === requiredCount
      && requiredCount > 0
      && (!requireStaff || selected.every((sid) => staffByService[sid]));

  const confirm = () => {
    if (!canConfirm) return;
    if (pendingSwapMode) {
      const rows = (swapRows || []).map((row) => {
        const svc = eligible.find((s) => s.service_id === row.service_id);
        const name = isAr
          ? (svc?.name_ar || row.name_ar || row.name)
          : (svc?.name_en || row.name_en || row.name);
        return {
          service_id: row.service_id,
          name,
          name_en: svc?.name_en || row.name_en || name,
          name_ar: svc?.name_ar || row.name_ar || name,
          employee_user_id: staffByService[row.service_id] || row.employee_user_id || null,
          status: row.status || "redeemed",
          ...(row.status === "pending" ? {
            scheduled_date: row.scheduled_date || consumptionByService[row.service_id]?.scheduled_date || defaultFollowUpDate(defaultPendingDays),
          } : {}),
        };
      });
      onConfirm(rows);
      onClose?.();
      return;
    }
    const rows = selected.map((serviceId) => {
      const selInit = (initialSelections || []).find((s) => s.service_id === serviceId);
      const svc = eligible.find((s) => s.service_id === serviceId);
      const name = isAr
        ? (svc?.name_ar || selInit?.name_ar || selInit?.name || svc?.name_en)
        : (svc?.name_en || selInit?.name_en || selInit?.name || svc?.name_ar);
      const consumption = consumptionByService[serviceId] || { done: true };
      const row = {
        service_id: serviceId,
        name,
        name_en: svc?.name_en || selInit?.name_en || name,
        name_ar: svc?.name_ar || selInit?.name_ar || name,
        employee_user_id: staffByService[serviceId] || null,
      };
      if (trackConsumption) {
        row.status = consumption.done !== false ? "redeemed" : "pending";
        if (row.status === "pending") {
          row.scheduled_date = consumption.scheduled_date || defaultFollowUpDate(defaultPendingDays);
        }
      }
      return row;
    });
    onConfirm(rows);
    onClose?.();
  };

  const visibleServices = selectionLocked
    ? eligible.filter((s) => selected.includes(s.service_id))
    : eligible;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#2B1C1C]/50 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => onBackdropMouseDown(e, onClose)}
      data-testid="pick-package-modal"
    >
      <div className="card-surface w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl gold-text">
              {isAr ? pkg.name_ar : pkg.name_en}
            </h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {pendingSwapMode
                ? (isAr ? "استبدل الخدمات غير المنجزة فقط — المنجزة مقفلة" : "Replace pending services only — completed ones are locked")
                : selectionLocked
                ? (isAr ? "حدد الخدمات المنجزة اليوم والباقي للمتابعة" : "Mark which services were done today vs follow-up")
                : (isAr
                  ? `اختر ${pickCount} خدمات من القائمة أدناه`
                  : `Choose ${pickCount} service(s) from the list below`)}
            </p>
            {trackConsumption && !pendingSwapMode && (
              <p className="text-xs text-[var(--gold)] mt-1">
                {isAr
                  ? "الخدمات غير المنجزة اليوم ستظهر في لوحة المتابعة"
                  : "Services not done today will appear on the dashboard follow-up list"}
              </p>
            )}
            {!selectionLocked && !pendingSwapMode && (
              <p className="text-xs text-[var(--text-muted)] mt-1 num-display">
                {selected.length}/{pickCount} {isAr ? "محدد" : "selected"}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2">
          {pendingSwapMode ? (swapRows || []).map((row) => {
            const sid = row.service_id;
            const isPending = row.status === "pending";
            const label = selectedServiceLabel(row, lang);
            const opts = optionsForPendingRow(row);
            return (
              <div key={sid} className={`rounded-lg border p-3 ${isPending ? "border-[var(--gold)] bg-[var(--gold-tint)]" : "border-[var(--border-subtle)] opacity-80"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm text-[var(--text)]">{label}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isPending ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                    {isPending ? (isAr ? "معلق" : "Pending") : (isAr ? "منجزة" : "Done")}
                  </span>
                </div>
                {isPending && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="text-[10px] text-[var(--text-muted)] block mb-1">
                        {isAr ? "استبدال بـ" : "Replace with"}
                      </label>
                      <select
                        className="input-dark text-sm w-full"
                        value={sid}
                        onChange={(e) => updateSwapRow(sid, { service_id: e.target.value })}
                        data-testid={`swap-package-service-${sid}`}
                      >
                        {opts.map((s) => (
                          <option key={s.service_id} value={s.service_id}>
                            {isAr ? s.name_ar : s.name_en}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[var(--text-muted)] block mb-1">{isAr ? "الموظف" : "Staff"}</label>
                      <select
                        className="input-dark text-sm w-full"
                        value={staffByService[sid] || ""}
                        onChange={(e) => setStaffByService((m) => ({ ...m, [sid]: e.target.value || null }))}
                      >
                        <option value="">{isAr ? "— اختر موظف —" : "— Select staff —"}</option>
                        {(staff || []).map((u) => (
                          <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          }) : visibleServices.map((svc) => {
            const sid = svc.service_id;
            const isOn = selected.includes(sid);
            const disabled = !selectionLocked && !isOn && selected.length >= pickCount;
            const label = isAr ? svc.name_ar : svc.name_en;
            const consumption = consumptionByService[sid] || { done: true, scheduled_date: defaultFollowUpDate(defaultPendingDays) };
            return (
              <div
                key={sid}
                className={`rounded-lg border p-3 transition ${isOn ? "border-[var(--gold)] bg-[var(--gold-tint)]" : "border-[var(--border-subtle)]"} ${disabled ? "opacity-50" : ""}`}
              >
                {selectionLocked ? (
                  <div className="font-medium text-sm text-[var(--text)]">{label}</div>
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(sid)}
                    data-testid={`pick-package-service-${sid}`}
                    className="w-full text-start"
                  >
                    <div className="font-medium text-sm text-[var(--text)]">{label}</div>
                  </button>
                )}
                {isOn && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="text-[10px] text-[var(--text-muted)] block mb-1">
                        {isAr ? "الموظف" : "Staff"}
                      </label>
                      <select
                        className="input-dark text-sm w-full"
                        value={staffByService[sid] || ""}
                        onChange={(e) => setStaffByService((m) => ({ ...m, [sid]: e.target.value || null }))}
                        data-testid={`pick-package-staff-${sid}`}
                      >
                        <option value="">{isAr ? "— اختر موظف —" : "— Select staff —"}</option>
                        {(staff || []).map((u) => (
                          <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>
                        ))}
                      </select>
                    </div>
                    {trackConsumption && (
                      <div className="rounded-md border border-[var(--border-subtle)] p-2 bg-[var(--surface)]">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consumption.done !== false}
                            onChange={(e) => setDone(sid, e.target.checked)}
                            data-testid={`pick-package-done-${sid}`}
                          />
                          <span>{isAr ? "تمت اليوم" : "Done today"}</span>
                        </label>
                        {consumption.done === false && (
                          <div className="mt-2">
                            <label className="text-[10px] text-[var(--text-muted)] block mb-1">
                              {isAr ? "موعد المتابعة" : "Follow-up date"}
                            </label>
                            <input
                              type="date"
                              className="input-dark text-sm w-full"
                              value={consumption.scheduled_date || defaultFollowUpDate(defaultPendingDays)}
                              onChange={(e) => setConsumptionByService((m) => ({
                                ...m,
                                [sid]: { ...consumption, done: false, scheduled_date: e.target.value },
                              }))}
                              data-testid={`pick-package-date-${sid}`}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {visibleServices.length === 0 && (
            <div className="text-sm text-center text-[var(--text-muted)] py-6">
              {isAr ? "لا توجد خدمات مؤهلة لهذه الباقة" : "No eligible services configured for this package"}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-[var(--border)]">
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!canConfirm}
            data-testid="pick-package-confirm"
            className="btn-gold px-5 py-2 rounded-md disabled:opacity-50"
          >
            {confirmLabel || (selectionLocked
              ? (isAr ? "متابعة" : "Continue")
              : (isAr ? "إضافة للسلة" : "Add to cart"))}
          </button>
        </div>
      </div>
    </div>
  );
}
